import React, { useEffect, useRef } from "react";

/**
 * Custom cursor:
 *  • Small filled dot that follows the pointer 1:1
 *  • Outer ring that trails with spring easing
 *  • Both grow + change color when hovering links / buttons
 *  • Soft cyan glow halo follows behind for the "futuristic" vibe
 *  • Auto-disabled on touch devices (native cursor used instead)
 */
const CursorGlow = () => {
    const dotRef = useRef(null);
    const ringRef = useRef(null);
    const glowRef = useRef(null);
    const pos = useRef({ x: -100, y: -100, tx: -100, ty: -100 });

    useEffect(() => {
        const dot = dotRef.current;
        const ring = ringRef.current;
        const glow = glowRef.current;
        if (!dot || !ring || !glow) return;

        // Touch devices → bail, keep native cursor
        if (window.matchMedia("(pointer: coarse)").matches) {
            dot.style.display = "none";
            ring.style.display = "none";
            glow.style.display = "none";
            return;
        }

        // Hide the native cursor on all elements
        document.documentElement.style.cursor = "none";
        const styleTag = document.createElement("style");
        styleTag.id = "custom-cursor-style";
        styleTag.innerHTML = `
            *, *::before, *::after { cursor: none !important; }
            input, textarea, [contenteditable="true"] { cursor: text !important; }
        `;
        document.head.appendChild(styleTag);

        let raf;
        const onMove = (e) => {
            pos.current.tx = e.clientX;
            pos.current.ty = e.clientY;
            // dot follows immediately
            dot.style.transform = `translate3d(${e.clientX - 4}px, ${e.clientY - 4}px, 0)`;
        };

        const tick = () => {
            const p = pos.current;
            p.x += (p.tx - p.x) * 0.18;
            p.y += (p.ty - p.y) * 0.18;
            ring.style.transform = `translate3d(${p.x - 18}px, ${p.y - 18}px, 0)`;
            glow.style.transform = `translate3d(${p.x - 150}px, ${p.y - 150}px, 0)`;
            raf = requestAnimationFrame(tick);
        };
        tick();
        window.addEventListener("mousemove", onMove);

        // Interactive elements → grow ring + glow
        const interactiveSelector =
            'a, button, [role="button"], [data-cursor="hover"], input[type="submit"]';
        const onOver = (e) => {
            if (e.target.closest(interactiveSelector)) {
                ring.classList.add("cursor-hover");
                glow.classList.add("cursor-hover-glow");
            }
        };
        const onOut = (e) => {
            if (e.target.closest(interactiveSelector)) {
                ring.classList.remove("cursor-hover");
                glow.classList.remove("cursor-hover-glow");
            }
        };
        document.addEventListener("mouseover", onOver);
        document.addEventListener("mouseout", onOut);

        // Hide when leaving window
        const onLeave = () => {
            dot.style.opacity = "0";
            ring.style.opacity = "0";
            glow.style.opacity = "0";
        };
        const onEnter = () => {
            dot.style.opacity = "1";
            ring.style.opacity = "1";
            glow.style.opacity = "1";
        };
        document.addEventListener("mouseleave", onLeave);
        document.addEventListener("mouseenter", onEnter);

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener("mousemove", onMove);
            document.removeEventListener("mouseover", onOver);
            document.removeEventListener("mouseout", onOut);
            document.removeEventListener("mouseleave", onLeave);
            document.removeEventListener("mouseenter", onEnter);
            document.documentElement.style.cursor = "";
            const el = document.getElementById("custom-cursor-style");
            if (el) el.remove();
        };
    }, []);

    return (
        <>
            <div
                ref={glowRef}
                aria-hidden
                className="cursor-glow pointer-events-none fixed left-0 top-0 z-[60] mix-blend-screen"
            />
            <div
                ref={ringRef}
                aria-hidden
                className="cursor-ring pointer-events-none fixed left-0 top-0 z-[61]"
            />
            <div
                ref={dotRef}
                aria-hidden
                className="cursor-dot pointer-events-none fixed left-0 top-0 z-[62]"
            />
        </>
    );
};

export default CursorGlow;
